'use client'

import type { NotifySender, SentenceSegment } from '@ordo/notifications-client/contract'
import {
  Bell,
  Calendar,
  Heart,
  House,
  type LucideIcon,
  Monitor,
  Share2,
  SquareCheck,
  Trophy,
  Utensils,
} from 'lucide-react'
import type * as React from 'react'

import { cn } from '../cn'
import { NOTIFICATION_GENERIC_CIRCLE_CLASS } from '../notification-generic-circle'
import { initialsOf, notificationAppLabel } from '../notification-inbox'

export { NOTIFICATION_GENERIC_CIRCLE_CLASS }

/**
 * The surface a notification row sits on. Rows tint it (unread, selected) and
 * the app badge and stacked faces draw a ring in the row's own colour, so the
 * ring reads as a cut-out on every background in light and dark.
 */
export const NOTIFICATION_ROW_COLOR = 'var(--notification-row, var(--notification-surface, var(--color-background)))'

const APP_VISUALS: Record<string, { tile: string; Icon: LucideIcon }> = {
  tasks: { tile: 'bg-blue-500', Icon: SquareCheck },
  rfps: { tile: 'bg-indigo-500', Icon: Trophy },
  customers: { tile: 'bg-rose-600', Icon: Heart },
  factory: { tile: 'bg-amber-600', Icon: Share2 },
  schedule: { tile: 'bg-orange-600', Icon: Calendar },
  menus: { tile: 'bg-green-600', Icon: Utensils },
  previews: { tile: 'bg-cyan-600', Icon: Monitor },
  hq: { tile: 'bg-slate-600', Icon: House },
}

const FALLBACK_VISUAL = { tile: 'bg-slate-500', Icon: Bell }

export function notificationAppVisual(app: string): { tile: string; Icon: LucideIcon } {
  return APP_VISUALS[app] ?? FALLBACK_VISUAL
}

const PERSON_COLORS = [
  'bg-sky-700',
  'bg-teal-700',
  'bg-violet-700',
  'bg-pink-800',
  'bg-amber-700',
  'bg-slate-600',
  'bg-emerald-700',
  'bg-indigo-700',
]

const ORG_INKS = ['text-blue-900 ring-blue-900', 'text-red-900 ring-red-900', 'text-emerald-800 ring-emerald-800']

function pick<T>(list: readonly T[], key: string): T {
  let hash = 0
  for (const char of key) {
    hash = (hash * 31 + char.charCodeAt(0)) >>> 0
  }
  return list[hash % list.length] as T
}

type Face = Exclude<NotifySender, { kind: 'none' }>

function FaceCircle({ sender, size, ring }: { sender: Face; size: number; ring?: boolean }) {
  const style: React.CSSProperties = {
    width: size,
    height: size,
    fontSize: Math.round(size * (sender.kind === 'org' ? 0.27 : 0.36)),
    ...(ring ? { boxShadow: `0 0 0 2px ${NOTIFICATION_ROW_COLOR}` } : {}),
  }
  if (sender.imageUrl) {
    return (
      <img
        src={sender.imageUrl}
        alt=""
        aria-hidden="true"
        className="block shrink-0 rounded-full bg-muted object-cover"
        style={style}
      />
    )
  }
  const key = sender.id || sender.name
  return (
    <span
      aria-hidden="true"
      style={style}
      className={cn(
        'grid shrink-0 place-items-center rounded-full leading-none',
        sender.kind === 'org'
          ? cn('bg-white font-extrabold ring-2 ring-inset', pick(ORG_INKS, key))
          : cn('font-semibold text-white', pick(PERSON_COLORS, key)),
      )}>
      {initialsOf(sender.name)}
    </span>
  )
}

/** The generic circle: no person and no school, the same in every app. */
export function NotificationGenericCircle({ size = 40, className }: { size?: number; className?: string }) {
  return (
    <span
      aria-hidden="true"
      data-notification-generic=""
      className={cn('grid shrink-0 place-items-center rounded-full', NOTIFICATION_GENERIC_CIRCLE_CLASS, className)}
      style={{ width: size, height: size }}>
      <Bell style={{ width: Math.round(size * 0.48), height: Math.round(size * 0.48) }} />
    </span>
  )
}

export interface NotificationPictureProps {
  /** Who the picture shows. Two or more distinct people stack; none draws the generic circle. */
  senders: readonly NotifySender[]
  /** The owning app, drawn as a badge on the corner. `null` for the Following group line, which spans apps. */
  app?: string | null
  size?: number
  className?: string
}

/** A person, else the school or chef involved, else the generic circle; the app badge sits on the corner. */
export function NotificationPicture({ senders, app = null, size = 40, className }: NotificationPictureProps) {
  const faces = senders.filter((sender): sender is Face => sender.kind !== 'none')
  const badge = Math.round(size * 0.45)
  const visual = app ? notificationAppVisual(app) : null
  const small = Math.round(size * 0.72)
  return (
    <span className={cn('relative block shrink-0', className)} style={{ width: size, height: size }}>
      {faces.length === 0 ? (
        <NotificationGenericCircle size={size} />
      ) : faces.length === 1 ? (
        <FaceCircle sender={faces[0] as Face} size={size} />
      ) : (
        <>
          <span className="absolute left-0 top-0">
            <FaceCircle sender={faces[0] as Face} size={small} ring />
          </span>
          <span className="absolute bottom-0 right-0">
            <FaceCircle sender={faces[1] as Face} size={small} ring />
          </span>
        </>
      )}
      {visual && app ? (
        <span
          title={notificationAppLabel(app)}
          className={cn('absolute -bottom-1 -right-1 grid place-items-center text-white', visual.tile)}
          style={{
            width: badge,
            height: badge,
            borderRadius: Math.round(badge * 0.3),
            boxShadow: `0 0 0 2px ${NOTIFICATION_ROW_COLOR}`,
          }}>
          <visual.Icon aria-hidden="true" strokeWidth={2.6} style={{ width: badge * 0.6, height: badge * 0.6 }} />
        </span>
      ) : null}
    </span>
  )
}

/** The app's square tile, used on the item card ("Tasks · Task  /  Sign debt term sheet  /  Open in Tasks"). */
export function NotificationAppTile({ app, size = 40 }: { app: string; size?: number }) {
  const visual = notificationAppVisual(app)
  return (
    <span
      aria-hidden="true"
      className={cn('grid shrink-0 place-items-center text-white', visual.tile)}
      style={{ width: size, height: size, borderRadius: Math.round(size / 4) }}>
      <visual.Icon style={{ width: size * 0.45, height: size * 0.45 }} />
    </span>
  )
}

/** The sentence: names and the item in bold, the rest slightly dimmer. */
export function NotificationSentence({
  segments,
  className,
}: {
  segments: readonly SentenceSegment[]
  className?: string
}) {
  return (
    <span className={cn('text-foreground/80', className)}>
      {segments.map((segment, index) =>
        segment.bold ? (
          <span key={index} className="font-semibold text-foreground">
            {segment.text}
          </span>
        ) : (
          <span key={index}>{segment.text}</span>
        ),
      )}
    </span>
  )
}
